import { createContext, useContext } from "react";
import type { ReactNode, ComponentProps } from "react";
import * as PopoverPrimitive from "@radix-ui/react-popover";
import { PortalContextReset } from "../Controls/Internal/PortalContextReset";
import { usePopoverZIndex } from "../Theme/overlayZIndex";

type PopoverMode = "portal" | "inline";
const PopoverModeContext = createContext<PopoverMode>("portal");
export const PopoverModeProvider = PopoverModeContext.Provider;

type PopoverRootProps = ComponentProps<typeof PopoverPrimitive.Root>;
type PopoverTriggerProps = ComponentProps<typeof PopoverPrimitive.Trigger>;
type PopoverAnchorProps = ComponentProps<typeof PopoverPrimitive.Anchor>;

type PopoverWidth = "trigger" | "min-trigger";
type PopoverPadding = "none" | "sm" | "lg";
type PopoverVariant = "card" | "dark" | "bare";

const widthStyles: Record<PopoverWidth, string> = {
  trigger: "w-[var(--radix-popover-trigger-width)]",
  "min-trigger": "min-w-[var(--radix-popover-trigger-width)]",
};

const paddingStyles: Record<PopoverPadding, string> = {
  none: "",
  sm: "px-1 py-1",
  lg: "p-4",
};

const variantStyles: Record<PopoverVariant, string> = {
  card: "border border-border bg-card",
  dark: "bg-gray-900",
  // No chrome — for content that brings its own card (border/bg), e.g. a
  // colour-tinted StickyInfoBox. Keeps just the rounded clip + float shadow.
  bare: "",
};

interface PopoverContentProps {
  children: ReactNode;
  side?: "top" | "bottom" | undefined;
  sideOffset?: number | undefined;
  align?: "start" | "end" | "center" | undefined;
  width?: PopoverWidth | undefined;
  padding?: PopoverPadding | undefined;
  variant?: PopoverVariant | undefined;
  onMouseEnter?: React.MouseEventHandler | undefined;
  onMouseLeave?: React.MouseEventHandler | undefined;
  onInteractOutside?: ComponentProps<
    typeof PopoverPrimitive.Content
  >["onInteractOutside"];
  onWheel?: React.WheelEventHandler | undefined;
}

function PopoverContent_({
  children,
  side = "bottom",
  sideOffset = 4,
  align = "start",
  width,
  padding = "none",
  variant = "card",
  onMouseEnter,
  onMouseLeave,
  onInteractOutside,
  onWheel,
}: PopoverContentProps) {
  const popoverZ = usePopoverZIndex();
  const mode = useContext(PopoverModeContext);
  // overflow-hidden is what makes `rounded-lg` an actual clip rather than
  // just a rounded border. Without it, an edge-to-edge child with square
  // corners (a menu row's hover fill, a table's first header cell) paints
  // over the rounded corners and pokes out past them. Nothing inside a
  // popover needs to escape its box — submenus and nested popovers portal
  // out at the Radix layer, above this element.
  const baseStyles = `overflow-hidden rounded-lg shadow-floating ${variantStyles[variant]} ${paddingStyles[padding]} ${width ? widthStyles[width] : ""}`;

  // A popover is its own layout root — content must not inherit the layout
  // contexts (table, control bar, …) of the surface its trigger lives in;
  // see PortalContextReset. Applied in BOTH modes so inline (Storybook)
  // snapshots match the portaled runtime rendering.
  if (mode === "inline") {
    return (
      <div className={`mt-2 ${baseStyles}`}>
        <PortalContextReset>{children}</PortalContextReset>
      </div>
    );
  }

  return (
    <PopoverPrimitive.Portal>
      <PopoverPrimitive.Content
        style={{ zIndex: popoverZ }}
        side={side}
        sideOffset={sideOffset}
        align={align}
        onOpenAutoFocus={(e) => e.preventDefault()}
        {...(onInteractOutside && { onInteractOutside })}
        {...(onMouseEnter && { onMouseEnter })}
        {...(onMouseLeave && { onMouseLeave })}
        {...(onWheel && { onWheel })}
        className={`data-[state=closed]:animate-popover-out data-[state=open]:animate-popover-in ${baseStyles}`}
      >
        <PortalContextReset>{children}</PortalContextReset>
      </PopoverPrimitive.Content>
    </PopoverPrimitive.Portal>
  );
}

function PopoverRoot_(props: PopoverRootProps) {
  return <PopoverPrimitive.Root {...props} />;
}

function PopoverTrigger_(props: PopoverTriggerProps) {
  return <PopoverPrimitive.Trigger {...props} />;
}

function PopoverAnchor_(props: PopoverAnchorProps) {
  return <PopoverPrimitive.Anchor {...props} />;
}

export const Popover = Object.assign(PopoverRoot_, {
  Trigger: PopoverTrigger_,
  Anchor: PopoverAnchor_,
  Content: PopoverContent_,
});
